import React, { useEffect, useMemo } from "react";

import { ChronoUnit, ZonedDateTime, convert } from "@js-joda/core";
import CalendarHeatmap from "components/dashboard/calendar-heatmap/CalendarHeatmap";
import { AnalyticsResultDto, MdmObjectDto, useGetAnalyticsByObjectIdQuery, useGetAnalyticsByProjectIdAndDateRangeQuery } from "gql/generated/types";
import { Link } from "react-router-dom";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { getFirstDayOfHalfYear, getLastDayOfCurrentMonthDate } from "utils/joda-time-utils";
import { TableColumn } from "utils/types";
import ItemsTable from "./ItemsTable";
import { renderSeverity, rowDropdownFilterTemplate, severityRowFilterTemplate } from "../utils/utils";

type ObjectAnalyticsProps = {
  object?: MdmObjectDto;
  projectId?: string;
  tableName: string
};

const ObjectAnalytics: React.FC<ObjectAnalyticsProps> = ({ object, projectId, tableName }) => {
  const [dateFrom, setDateFrom] = React.useState<Date>(getFirstDayOfHalfYear());
  const [dateTo, setDateTo] = React.useState<Date>(getLastDayOfCurrentMonthDate());
  const [filterData, setFilterData] = React.useState<AnalyticsResultDto[]>([]);

  const {data: analytics, loading} = useGetAnalyticsByObjectIdQuery({
    variables: {
      objectId: object?.id!,
      range: {
        start: dateFrom?.toISOString(),
        end: dateTo?.toISOString(),
        endInclusive: true,
        startInclusive: true
      }
    },
    skip: !object?.id
  })

  const {data, loading: loading2} = useGetAnalyticsByProjectIdAndDateRangeQuery({
    variables: {
      projectId: projectId!,
      range: {
        start: dateFrom?.toISOString(),
        end: dateTo?.toISOString(),
        endInclusive: true,
        startInclusive: true
      }
    },
    skip: !projectId
  })

  const analyticsData = useMemo(
    () =>
      (analytics?.allAnalyticsResultsByObjectIdAndDateRange ??
        data?.allAnalyticsResultsByProjectIdAndDateRange ??
        []) as AnalyticsResultDto[],
    [analytics, data]
  );

  const isLoading = loading || loading2;

  useEffect(() => {
    setFilterData(analyticsData);
  }, [analyticsData])
  
  const renderObjectLink = (rowData: AnalyticsResultDto) => (
    <Link to={`/mdm/${projectId}/objects/${rowData.mdmObject?.id}`}>{rowData.mdmObject?.title}</Link>
  );

  const renderDeviceLink = (rowData: AnalyticsResultDto) => (
    <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice?.id}`}>{rowData.mdmDevice?.identifier}</Link>
  );

  const commonColumns: TableColumn<AnalyticsResultDto>[] = [
    {
      field: "id.date",
      header: "Date",
      sortable: true,
      body: rowData => ZonedDateTime.parse(rowData.id?.date).format(DEFAULT_DATE_FORMATTER),
      columnKey: "date",
    },
    {
      field: "severity",
      header: "Severity",
      body: renderSeverity,
      filter: true,
      filterElement: severityRowFilterTemplate,
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
      columnKey: "severity",
    },
    {
      field: "id.analyticsType",
      header: "Analytic Type",
      filter: true,
      filterElement: o =>
        rowDropdownFilterTemplate(o, [
          ...new Set(analyticsData.map(item => item.id?.analyticsType)),
        ]),
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
      columnKey: "analyticType",
    },
    { field: "description", header: "Description", columnKey: "description" },
    {
      field: "deviceType",
      header: "Device Type",
      filter: true,
      filterElement: o =>
        rowDropdownFilterTemplate(o, [...new Set(analyticsData.map(item => item.deviceType))]),
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
      columnKey: "deviceType",
    },
    {
      field: "mdmDevice.identifier",
      header: "Device",
      body: renderDeviceLink,
      hideInFile: true,
      columnKey: "device",
    },
  ];

  const columns: TableColumn<AnalyticsResultDto>[] = !object
    ? [
        ...commonColumns,
        {
          field: "mdmObject.id",
          header: "Object",
          body: renderObjectLink,
          hideInFile: true,
          columnKey: "object",
        },
      ]
    : [...commonColumns];

  const formatHeatData = filterData.reduce((prev, curr) => {
    const date = convert(ZonedDateTime.parse(curr.id?.date).truncatedTo(ChronoUnit.DAYS)).toDate();
    const heatDay = prev.find(day => day.date.getTime() === date.getTime());
    if (heatDay) {
      heatDay.value += 1;
    } else {
      prev.push({date, value: 1});
    }
    return prev;
  }, [] as { date: Date; value: number }[]);

  return (
    <div className="mt-4">
      <CalendarHeatmap
        startDate={dateFrom}
        endDate={dateTo}
        dataset={formatHeatData}
        setStartDate={setDateFrom}
        setEndDate={setDateTo}
        highValueColor="ed0000"
        lowValueColor="ffffff"
        minValue={0}
        maxValue={5}
        staticMinMaxColors
        staticMinMaxTitles={false}
        staticMinMaxValues
        isCustomDataset
        showDateSelection
      />
      <div className="mt-3">
        <ItemsTable
          columns={columns}
          data={analyticsData}
          loading={isLoading}
          showToolbarBtn={false}
          tableName={tableName}
          searchFields={[
            "id.date",
            "severity",
            "id.analyticsType",
            "deviceType",
            "description",
            "mdmDevice.identifier",
            "mdmObject.title",
          ]}
          setFilteredData={setFilterData}
        />
      </div>
    </div>
  );
}

export default ObjectAnalytics;